import { getBaseUrl } from "./base-url";
import {
  canManageMembers,
  membersPath,
  type ViewerRole,
} from "./member-access";

/** Shown when sign-in cannot claim the database the guest asked to share. */
export const GUEST_SHARE_DENIED = "A different guest owns that database.";

const TOKEN_KEY = "od:guest-share-token";
const DS_KEY = "od:guest-share-datasource";
const RETURN_KEY = "od:guest-share-return";

type PendingShare = {
  token: string;
  datasourceId: string;
  returnPath: string;
};

let memory: PendingShare | null = null;
let blocked = false;

export type ShareCard = {
  id?: string | null;
  isDemo?: boolean | null;
  type?: string | null;
  projectId?: string | null;
  viewerRole?: string | null;
  isGuest: boolean;
};

/**
 * Where Share goes.
 *
 * An owner opens the members page. A guest Postgres card with no project
 * opens sign-in and keeps that members page as the return path. Everyone
 * else gets no Share control.
 */
export function shareTarget(card: ShareCard): string | undefined {
  const id = card.id?.trim();
  if (!id || card.isDemo) return undefined;
  if (canManageMembers(card.viewerRole as ViewerRole)) {
    return membersPath(id);
  }
  if (
    card.isGuest &&
    card.type === "POSTGRESQL" &&
    !card.projectId &&
    !card.viewerRole
  ) {
    return `/auth?next=${encodeURIComponent(membersPath(id))}`;
  }
  return undefined;
}

/** Datasource id only when the href is the guest sign-in return. */
export function datasourceIdFromShareHref(
  href: string | undefined,
): string | undefined {
  if (!href) return undefined;
  const queryAt = href.indexOf("?");
  if (queryAt < 0) return undefined;
  const next = new URLSearchParams(href.slice(queryAt + 1)).get("next") ?? "";
  const match = next.match(/^\/datasources\/([^/]+)\/members$/);
  return match?.[1];
}

function browserSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Remember the guest token before the sign-in screen replaces the session. */
export function keepGuestForShare(
  token: string,
  href: string | undefined,
): void {
  const datasourceId = datasourceIdFromShareHref(href);
  if (!token || !datasourceId || !href) return;
  const queryAt = href.indexOf("?");
  const returnPath =
    new URLSearchParams(href.slice(queryAt + 1)).get("next") ?? "";
  if (!returnPath.startsWith("/") || returnPath.startsWith("//")) return;
  memory = { token, datasourceId, returnPath };
  blocked = false;
  const session = browserSession();
  if (!session) return;
  try {
    session.setItem(TOKEN_KEY, token);
    session.setItem(DS_KEY, datasourceId);
    session.setItem(RETURN_KEY, returnPath);
  } catch {
    // The in-memory copy is enough for this tab.
  }
}

function readPending(): PendingShare | null {
  if (memory?.token && memory.datasourceId && memory.returnPath) return memory;
  const session = browserSession();
  if (!session) return null;
  try {
    const token = session.getItem(TOKEN_KEY);
    const datasourceId = session.getItem(DS_KEY);
    const returnPath = session.getItem(RETURN_KEY);
    if (!token || !datasourceId || !returnPath) return null;
    return { token, datasourceId, returnPath };
  } catch {
    return null;
  }
}

function clearPending(): void {
  memory = null;
  const session = browserSession();
  if (!session) return;
  try {
    session.removeItem(TOKEN_KEY);
    session.removeItem(DS_KEY);
    session.removeItem(RETURN_KEY);
  } catch {
    // Already dropped from memory.
  }
}

export type GuestShareLink = {
  ok: boolean;
  returnPath: string | null;
};

/**
 * Link the guest session that asked to share, using the new user token.
 *
 * Call this before the auth store publishes that token. Returns null when
 * Share was not the reason for this sign-in, so a wizard `next` still wins.
 * A 404 or a network miss sets the blocked flag and does not return the
 * members path.
 */
export async function linkPendingGuest(
  userToken: string,
): Promise<GuestShareLink | null> {
  const pending = readPending();
  if (!pending || !userToken) return null;
  clearPending();
  try {
    const response = await fetch(`${getBaseUrl()}/auth/guest/attach`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        guestToken: pending.token,
        datasourceId: pending.datasourceId,
      }),
    });
    if (!response.ok) {
      blocked = true;
      return { ok: false, returnPath: null };
    }
    blocked = false;
    return { ok: true, returnPath: pending.returnPath };
  } catch {
    blocked = true;
    return { ok: false, returnPath: null };
  }
}

/** True once, after a share link was refused. A normal sign-in stays false. */
export function guestShareWasBlocked(): boolean {
  const value = blocked;
  blocked = false;
  return value;
}
